/**
 * VirtualizedDiffRow - Renders a single diff file within a virtualized list.
 *
 * ## Measurement Strategy
 *
 * This component uses tanstack/react-virtual for virtualization. The virtualizer needs to know
 * each item's height, which can come from either:
 * 1. **Estimation** - via `estimateSize` callback (uses `estimateDiffSize`)
 * 2. **Measurement** - via `measureElement` ref (uses ResizeObserver)
 *
 * ### When Measurement is NOT Needed (Deterministic Height):
 *
 * - **Loading/Skeleton states**: Height is calculated from `estimateDiffSize()` based on
 *   line counts, comment threads, and annotations. No need to measure.
 *
 * - **Collapsed diffs**: Just header + collapsed indicator. Fixed height.
 *
 * ### When Measurement IS Needed (Dynamic Height):
 *
 * - **Loaded, expanded diffs**: Actual content varies from estimates. Once measured,
 *   the ResizeObserver continues watching for changes caused by:
 *   - User expands/collapses comment threads
 *   - User opens a new comment form (and types, growing the form)
 *   - Lines are expanded/collapsed
 *   - Annotations appear/disappear
 *
 * ### How ResizeObserver Works with measureElement:
 *
 * When `measureElement` is passed as a ref to an element:
 * 1. Initial measurement happens when the ref attaches
 * 2. A ResizeObserver is attached and continues observing
 * 3. Any height change triggers automatic re-measurement
 * 4. Observer is cleaned up when element unmounts
 *
 * This means we don't need special logic for "re-measure when threads change" -
 * the ResizeObserver handles it automatically.
 *
 * ### Scroll Position Adjustment:
 *
 * When item height changes, `shouldAdjustScrollPositionOnItemSizeChange` determines
 * whether to adjust scroll.
 */

import type {Repository} from '@github-ui/current-repository'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'
import {memo, type CSSProperties} from 'react'
import type {VirtualItem} from '@tanstack/react-virtual'
import type {ProgressiveDiffEntry} from '../types/progressive-diff-types'
import {DiffFileHeader} from '@github-ui/diff-file-header'
import type {DiffEntry} from '@github-ui/diff-lines'
import {DiffEntryLoadingSkeleton} from './progressive-diff/DiffEntryLoadingSkeleton'
import {PullRequestDiff} from './PullRequestDiff'
import {estimateDiffSize} from '../helpers/estimate-diff-size'
import {
  useCollapsedDiffStatus,
  useIsSelectedFile,
  useCommentsPreference,
  useLineSpacingPreference,
} from '../stores/selectors'
import {noop} from '@github-ui/noop'
import styles from './PullRequestVirtualizedDiffRow.module.css'
import diffStyles from '@github-ui/diff-lines/Diff.module.css'

/**
 * Firefox has a bug where text selection breaks when using `transform: translateY()`
 * on absolutely positioned elements. The selection is painted from the DOM position
 * (top: 0) rather than the visual position. Using `top` positioning directly fixes this.
 * We maintain `transform` for other browsers as it is generally preferred for perf reasons.
 * See: https://bugzilla.mozilla.org/show_bug.cgi?id=1913308
 */
// eslint-disable-next-line ssr-friendly/no-dom-globals-in-module-scope
const isFirefox = typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent)

interface VirtualizedDiffRowProps {
  virtualRow: VirtualItem
  virtualizedDiffEntry: ProgressiveDiffEntry | undefined
  diffEntry: DiffEntry | undefined
  diffSummary: PullRequestFileTreeDiff | undefined
  scrollMargin: number
  measureElement: (node: Element | null) => void
  headBranchName: string
  headRepoName?: string
  headRepoOwner?: string
  pullRequestGlobalRelayId: string
  repository: Pick<Repository, 'id' | 'name' | 'ownerLogin'>
  virtualWindowHeight?: number
  isOptimizedMeasurementEnabled?: boolean
}

const MemoizedVirtualizedDiffRow = memo(function VirtualizedDiffRow({
  virtualRow,
  virtualizedDiffEntry,
  diffEntry,
  diffSummary,
  scrollMargin,
  measureElement,
  headBranchName,
  headRepoName,
  headRepoOwner,
  pullRequestGlobalRelayId,
  repository,
  virtualWindowHeight,
  isOptimizedMeasurementEnabled,
}: VirtualizedDiffRowProps) {
  const isSelectedFile = useIsSelectedFile(diffEntry?.pathDigest)
  const commentPreference = useCommentsPreference()
  const lineSpacingPreference = useLineSpacingPreference()

  // Subscribe to the collapsed status directly so this component re-renders when it changes.
  // This ensures shouldMeasure is recalculated and measureElement ref is properly attached/detached.
  const isCollapsed = useCollapsedDiffStatus(virtualizedDiffEntry?.path ?? '')

  // Skeleton/loading states have deterministic height from estimateDiffSize
  const isStillLoading =
    virtualizedDiffEntry?.loadingStatus === 'NotLoaded' ||
    virtualizedDiffEntry?.loadingStatus === 'Loading' ||
    !diffEntry

  if (!virtualizedDiffEntry) return null

  const offset = virtualRow.start - scrollMargin

  // Firefox has broken text selection with transform positioning on absolute elements.
  // Use `top` positioning instead which Firefox handles correctly for selection painting.
  const virtualRowStyle: CSSProperties = isFirefox ? {top: `${offset}px`} : {transform: `translateY(${offset}px)`}

  // Show skeleton for NotLoaded and Loading states, or when diffEntry hasn't arrived yet
  if (isStillLoading) {
    const height = estimateDiffSize({
      diffSummary,
      commentPreference,
      lineSpacingPreference,
      isCollapsed,
    })

    const headerStickyOffset = -(virtualRow.start - scrollMargin)

    // Skeletons have deterministic height from estimateDiffSize - no measurement needed
    // When optimized measurement is disabled, attach measureElement for original behavior
    return (
      <div
        style={virtualRowStyle}
        className={styles.virtualizedDiffRow}
        ref={isOptimizedMeasurementEnabled ? undefined : measureElement}
        data-index={virtualRow.index}
        data-path-digest={virtualizedDiffEntry.pathDigest}
        data-estimated-height={height}
      >
        <div className={diffStyles.diffHeaderWrapper} style={{'--header-sticky-offset': `${headerStickyOffset}px`}}>
          <DiffFileHeader
            areLinesExpanded={false}
            canExpandOrCollapseLines={false}
            fileLinkHref={`#diff-${virtualizedDiffEntry.pathDigest}`}
            canToggleRichDiff={false}
            linesAdded={diffSummary?.linesAdded ?? 0}
            linesChanged={diffSummary?.linesChanged ?? 0}
            linesDeleted={diffSummary?.linesDeleted ?? 0}
            newPath={virtualizedDiffEntry.path}
            patchStatus={diffSummary?.changeType ?? ''}
            path={virtualizedDiffEntry.path}
            onToggleExpandAllLines={noop}
            onToggleFileCollapsed={noop}
            onToggleDiffDisplay={noop}
            isInSingleFileMode={false}
          />
        </div>
        <DiffEntryLoadingSkeleton
          ariaLabel={`Loading ${virtualizedDiffEntry.path}`}
          testId={`lazy-load-${virtualizedDiffEntry.path}`}
          id={`diff-${virtualizedDiffEntry.pathDigest}`}
          approximateDiffHeight={height}
          maximumSkeletonHeight={virtualWindowHeight ? virtualWindowHeight * 0.8 : undefined} // Limit skeleton height to 80% of the viewport height so that we see at least some of the surrounding context
        />
      </div>
    )
  }

  // At this point diffEntry is guaranteed to exist
  const loadedDiffEntry = diffEntry

  return (
    <div
      style={virtualRowStyle}
      className={styles.virtualizedDiffRow}
      ref={measureElement}
      data-index={virtualRow.index}
      data-path-digest={loadedDiffEntry.pathDigest}
    >
      <PullRequestDiff
        changeType={loadedDiffEntry.status}
        diffLines={loadedDiffEntry.diffLines}
        diffManuallyExpanded={false}
        diffSize={loadedDiffEntry.diffSize}
        headerStickyOffset={-(virtualRow.start - scrollMargin)}
        headBranchName={headBranchName}
        headRepoName={headRepoName}
        headRepoOwner={headRepoOwner}
        helpUrl={loadedDiffEntry.helpUrl}
        isBinary={loadedDiffEntry.isBinary}
        isSelected={isSelectedFile}
        isSubmodule={loadedDiffEntry.isSubmodule}
        isTooBig={loadedDiffEntry.isTooBig}
        linesAdded={loadedDiffEntry.linesAdded}
        linesChanged={loadedDiffEntry.linesChanged}
        linesDeleted={loadedDiffEntry.linesDeleted}
        newCommitOid={loadedDiffEntry.newCommitOid}
        newTreeEntry={loadedDiffEntry.newTreeEntry}
        objectId={loadedDiffEntry.objectId}
        oldCommitOid={loadedDiffEntry.oldCommitOid}
        oldTreeEntry={loadedDiffEntry.oldTreeEntry}
        path={loadedDiffEntry.path}
        pathDigest={loadedDiffEntry.pathDigest}
        pullRequestGlobalRelayId={pullRequestGlobalRelayId}
        repository={repository}
        reviewed={loadedDiffEntry.reviewed ?? false}
        richDiff={loadedDiffEntry.richDiff}
        status={loadedDiffEntry.status}
        submodule={loadedDiffEntry.submodule}
        truncatedReason={loadedDiffEntry.truncatedReason}
      />
    </div>
  )
})

export {MemoizedVirtualizedDiffRow as VirtualizedDiffRow}

try{ MemoizedVirtualizedDiffRow.displayName ||= 'MemoizedVirtualizedDiffRow' } catch {}